/**
 * The device form, grouped.
 *
 * Simpler than the item form, and deliberately so: a device is one box and how to reach it, where an
 * item is work plus proof plus ordering. So this is three short groups rather than six - what the box
 * is, how it is reached, and what the run expects of it - and the middle one is the only group
 * anybody edits twice.
 *
 * Extracted from the modal for the same reason the item form was: "+ New device" is offered from
 * inside the item form and from the builder tree, and those open a slide-over rather than navigating
 * away.
 *
 * A device belongs to no scenario. Every pod is built from the same template, so the box is a library
 * entry and the sections it appears in are attachments - many of them, edited here as a list. Which
 * is why nothing below refuses to save for want of a section: a device nothing has attached yet is a
 * catalogue entry, not a mistake.
 *
 * `credential_ref` names a credential; it never holds one. The agent inside the pod resolves the
 * reference on its own poll, against credentials scoped to that session and that pod - so a lab table
 * never contains a secret, and a compromised pod cannot reach past itself. A password typed into this
 * form would be shipped in a work order to every agent that enrols, which is why the field says what
 * it says and why nothing here accepts a username.
 */

import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import {
  LabDevice,
  LabDeviceType,
  LabScenario,
  LabScenarioDeviceLink,
  labDeviceBlockers,
  labSlugify,
  LAB_TRANSPORTS,
} from '../../support/labService';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabBlockerList,
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  LabProductSelect,
  LabRowSelect,
  LabSuggestedTextField,
} from './LabShared';
import { LabSpecBuilder } from './LabSpecFields';
import { LabSection } from './LabAdminShared';
import LabDeviceTypeEditorModal from './LabDeviceTypeEditorModal';

/**
 * A new device, ordered after whatever the section it was opened from already has.
 *
 * The scenario is read for one thing only - a sensible `order` - because ordering is a position in a
 * section's device list and the number has to start somewhere. It is not stored on the row: a device
 * has no section, and passing one here does not attach it. The caller does that, and from the library
 * there is nothing to pass.
 */
export const labDeviceEmpty = (scenario: LabScenario | null): Partial<LabDevice> => ({
  device_key: '',
  display_name: '',
  device_type: null,
  product_uid: null,
  product_name: '',
  platform: '',
  role: '',
  transport: 'ssh',
  address_template: '',
  port: null,
  credential_ref: '',
  jump_host: null,
  verify_tls: true,
  bootstrap_expected: true,
  required: true,
  monitored: true,
  order:
    ((scenario?.devices || []).reduce((max, device) => Math.max(max, device.order || 0), 0) || 0) +
    10,
});

/**
 * Why this device cannot be saved yet.
 *
 * The credential one is worth refusing on rather than warning about: a device with no credential
 * reference is one the agent cannot authenticate to, and the tempting fix - typing a password
 * somewhere - is the thing this table exists to prevent. Better to stop here than to leave a row that
 * looks finished and fails on the first poll.
 *
 * `manual` transport is exempt from both address and credential: nothing connects to it, because a
 * human does the step.
 *
 * The product is refused too, for a family type. "Cisco appliance" covers FMC, FTD and ISE, and each
 * speaks a different API - so a device that only says "appliance" is one no method can be generated
 * for, and the failure would surface at run time as a request against the wrong spec.
 */
export const labDeviceFormBlockers = (
  draft: Partial<LabDevice>,
  context: { deviceType?: LabDeviceType | null } = {},
): string[] => {
  const blockers: string[] = [];
  const manual = draft.transport === 'manual';

  // The type row when the caller has it, the saved row's own flag otherwise: an existing device
  // carries `device_type_requires_product` flattened by the serializer, so the check still holds
  // for a caller that renders the form without the catalogue.
  const requiresProduct = context.deviceType
    ? context.deviceType.requires_product
    : !!draft.device_type_requires_product;

  if (!draft.device_key?.trim()) {
    blockers.push(
      'A name with letters or digits in it. The key that steps and health reports name this box by is derived from it, and punctuation alone derives nothing.',
    );
  }

  if (requiresProduct && !draft.product_uid) {
    blockers.push(
      `Which product this is. ${
        context.deviceType?.name || 'The chosen type'
      } is a family, and each product in it has its own API.`,
    );
  }

  if (!manual && !draft.address_template?.trim()) {
    blockers.push(
      'An address. Without one there is nothing for the agent to connect to - unless every publication that uses this device overrides it.',
    );
  }

  if (!manual && !draft.credential_ref?.trim()) {
    blockers.push(
      'A credential reference: the name of a stored credential the pod resolves at poll time, never a password.',
    );
  }

  return blockers;
};

export type LabDeviceFormProps = {
  /**
   * The section the form was opened from, when there was one.
   *
   * Read for two things and neither is ownership: the ordering seed, and which box in the
   * attachment list starts ticked. Null from the Devices library, where nothing has been opened.
   */
  scenario: LabScenario | null;
  /** Every section, for the attachment list. */
  scenarios?: LabScenario[];
  device: LabDevice | null;
  draft: Partial<LabDevice>;
  onChange: (patch: Partial<LabDevice>) => void;
  deviceTypes: LabDeviceType[];
  /**
   * Which sections this device should be attached to when the caller saves.
   *
   * Held by the caller rather than in the draft, because attachment is not a column: it is rows in
   * the link table, written after the device is. Undefined hides the list - for a caller that
   * attaches on its own, like the builder's create-and-attach.
   */
  attachments?: number[];
  onAttachmentsChange?: (scenarioIds: number[]) => void;
  /** Cisco products, for a family type. Only fetched by callers that render the product select. */
  products?: { id: number; name: string }[];
  productsLoading?: boolean;
  /**
   * The device library, for the jump-host picker.
   *
   * Any device may be the route to any other now, so the candidates are not "the rest of this
   * section" - they are the library. Falls back to the opened section's devices for a caller that
   * has not fetched it.
   */
  libraryDevices?: LabDevice[];
  jsonError: string | null;
  onJsonError: (error: string | null) => void;
  /** Bumped by the caller when the draft is replaced, so the JSON editor reloads its text. */
  resetKey: number | string;
};

const LabDeviceForm: React.FC<LabDeviceFormProps> = ({
  scenario,
  scenarios,
  device,
  draft,
  onChange,
  deviceTypes,
  attachments,
  onAttachmentsChange,
  products,
  productsLoading,
  libraryDevices,
  jsonError,
  onJsonError,
  resetKey,
}) => {
  const sections = useMemo(() => scenarios || [], [scenarios]);
  const [newDeviceType, setNewDeviceType] = useState(false);

  /**
   * The key follows the name while the device is new, and is frozen the moment it exists.
   *
   * Frozen because it is a name, not an id: steps name devices by key, health reports are filed
   * against it, and another device's jump-host route points at this one through it. Renaming the box
   * in the library must not silently orphan any of that, so the key stops moving at the first save and
   * the form says so rather than leaving an author to discover it.
   */
  const setName = (display_name: string) =>
    onChange(device ? { display_name } : { display_name, device_key: labSlugify(display_name) });

  /** Whatever the library already calls these things, so the same answer is one click. */
  const library = useMemo(
    () => (libraryDevices?.length ? libraryDevices : scenario?.devices || []),
    [libraryDevices, scenario?.devices],
  );

  const platformsInUse = useMemo(
    () => library.map((candidate) => candidate.platform || '').filter(Boolean).sort(),
    [library],
  );

  const rolesInUse = useMemo(
    () => library.map((candidate) => candidate.role || '').filter(Boolean).sort(),
    [library],
  );

  const chosenType = useMemo(
    () => deviceTypes.find((candidate) => candidate.id === draft.device_type) || null,
    [deviceTypes, draft.device_type],
  );

  /**
   * The second select the user asked for, and the reason it is conditional.
   *
   * A type is either one product - named once on the type, for every device that claims it - or a
   * family, and then the product is a per-device answer. `requires_product` is the type saying which
   * arrangement it is, so this appears exactly when there is a question to ask.
   */
  const needsProduct = !!chosenType?.requires_product;

  /** What the chosen type says about this box, in one line, and nothing when it says neither. */
  const typeNote = useMemo(() => {
    if (!chosenType) return '';

    return [
      device ? '' : `Transport and port below were filled in from ${chosenType.name}.`,
      (chosenType.method_keys || []).length
        ? `Items here may only use: ${(chosenType.method_keys || []).join(', ')}.`
        : '',
    ]
      .filter(Boolean)
      .join(' ');
  }, [chosenType, device]);

  /** Anything in the library but itself - a device cannot be reached through itself. */
  const jumpHostCandidates = useMemo(
    () => library.filter((candidate) => candidate.id !== device?.id),
    [library, device?.id],
  );

  const jumpHost = useMemo(
    () => jumpHostCandidates.find((candidate) => candidate.id === draft.jump_host) || null,
    [jumpHostCandidates, draft.jump_host],
  );

  /**
   * Picking a type fills in what the type knows, without overwriting an answer.
   *
   * A device type carries a transport and a port because they are true of the platform, not of the
   * pod - so a new device gets them for free. An existing device is left alone: its transport was set
   * for a reason, and silently rewriting it on a type change is how a working device stops answering
   * with nothing in the UI to say what moved.
   *
   * The product is cleared either way, existing device included. It is the one field the old type
   * makes false rather than merely stale: an FMC id under a Windows type, or under another family, is
   * a device claiming to be something it is not, and the run would speak the wrong API to it.
   */
  const applyType = (id: number | null, resolved?: LabDeviceType | null) => {
    // `resolved` is for a type authored a moment ago: the catalogue query is still refetching, so
    // looking it up here would find nothing and the transport it declares would be dropped.
    const type = resolved ?? deviceTypes.find((candidate) => candidate.id === id) ?? null;

    if (!type || device) {
      onChange({ device_type: id, product_uid: null, product_name: '' });
      return;
    }

    onChange({
      device_type: id,
      product_uid: null,
      product_name: '',
      transport: type.default_transport || draft.transport,
      port: draft.port ?? type.default_port ?? null,
    });
  };

  const toggleAttachment = (id: number) => {
    const current = attachments || [];

    onAttachmentsChange?.(
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );
  };

  /**
   * What the row will be flagged for in the library once saved, run against the draft.
   *
   * The attachment set is turned into link rows first, so the jump-host check reads the sections
   * about to be true rather than the ones that were: tick a section the jump host is not in and the
   * warning appears before the save, which is the only point at which it is cheap to act on.
   */
  const rowBlockers = useMemo(() => {
    const links: LabScenarioDeviceLink[] = (attachments || []).map((id) => ({
      id: 0,
      scenario: id,
      device: device?.id || 0,
      enabled: true,
      notes: '',
      scenario_name: sections.find((candidate) => candidate.id === id)?.name,
    }));

    return labDeviceBlockers(
      {
        ...(labDeviceEmpty(scenario) as LabDevice),
        ...draft,
        device_type_requires_product: needsProduct,
        scenario_links: attachments ? links : device?.scenario_links,
      } as LabDevice,
      { jumpHost },
    );
  }, [draft, scenario, attachments, sections, device, needsProduct, jumpHost]);

  return (
    <div className="flex flex-col gap-4">
      <LabSection
        title="What this box is"
        hint="The library entry."
        actions={
          <StbButton role="add" size="xsmall" onClick={async () => setNewDeviceType(true)}>
            + New device type
          </StbButton>
        }
      >
        <TextField
          label="Name"
          value={draft.display_name || ''}
          onChange={(evt) => setName(evt.target.value)}
          helperText="What an admin reads in the lists, e.g. FTD 1."
          fullWidth
        />

        <LabDerivedKey
          value={draft.device_key || ''}
          frozen={!!device}
          label="Device key"
          usage="Steps, health reports and jump-host routes name the device by it."
        />

        {/* The type is what items are checked against; platform below it is free text for the
            version or build, which no check reads. */}
        <LabRowSelect
          label="Device type"
          value={draft.device_type}
          rows={deviceTypes}
          onChange={applyType}
          labelFor={(type) => `${type.name}${type.enabled ? '' : ' (disabled)'}`}
          hintFor={(type) =>
            `${type.default_transport}${type.default_port ? `:${type.default_port}` : ''}${
              type.requires_product
                ? ' - a family, so pick the product below'
                : type.product_name
                ? ` - ${type.product_name}`
                : ''
            }`
          }
          emptyLabel={deviceTypes.length ? 'No type declared' : 'No device types authored yet'}
          disabled={!deviceTypes.length}
          helpText="An item written for another kind is refused here. Unset means nothing is checked."
        />

        {/* Opens on choosing a family type, because that is when the type stops being an answer.
            The same control the item form uses for the product its spec was written for, so the two
            halves of that comparison are asked for the same way. Stored by value - uid plus a name
            snapshot - the way the type stores its own: the lab tables are mirrored into
            toolbox_dcloud, which has no product table for a key to point at. */}
        {needsProduct && (
          <LabProductSelect
            value={draft.product_uid}
            name={draft.product_name}
            products={products}
            loading={productsLoading}
            onChange={(product) =>
              onChange({ product_uid: product?.id ?? null, product_name: product?.name || '' })
            }
            helpText={`${chosenType?.name || 'This type'} covers several products with different APIs. Until this is answered nothing knows which one to speak to this box.`}
          />
        )}

        {!!typeNote && <p className="m-0 text-xs text-gray-500">{typeNote}</p>}

        {/* Selects seeded from the library, not closed lists. Nothing checks either column, which is
            exactly why they drift: the same box ends up windows, Windows and Windows Server 2022
            across three sections, and then filtering by platform finds a third of the estate. */}
        <LabFieldRow>
          <LabSuggestedTextField
            label="Platform"
            value={draft.platform}
            onChange={(platform) => onChange({ platform })}
            suggestions={platformsInUse}
            helpText="A version or a build under the type. Nothing checks it."
            placeholder="e.g. 7.4.1"
          />
          <LabSuggestedTextField
            label="Role"
            value={draft.role}
            onChange={(role) => onChange({ role })}
            suggestions={rolesInUse}
            helpText="What it is for in the story: edge firewall, victim host, jump box."
            placeholder="e.g. edge firewall"
          />
        </LabFieldRow>
      </LabSection>

      {/* Attachment, not ownership. Every pod is the same template, so the box exists once and the
          sections that use it tick it here. Editing the device from any of them edits it for all of
          them, which is the point and is worth saying on the screen. */}
      {!!attachments && (
        <LabSection
          title="Used in sections"
          hint="Attach the box to as many sections as use it - and through them, to every lab those sections are in."
        >
          {!sections.length && (
            <p className="m-0 text-xs text-gray-500">
              No sections authored yet. The device can be saved to the library and attached later.
            </p>
          )}

          {!!sections.length && (
            <div className="max-h-52 overflow-y-auto rounded border border-gray-100 p-2">
              {sections.map((candidate) => (
                <LabCheckbox
                  key={candidate.id}
                  label={`${candidate.name}${candidate.enabled ? '' : ' (disabled)'}`}
                  checked={(attachments || []).includes(candidate.id)}
                  onChange={() => toggleAttachment(candidate.id)}
                  helpText={`${candidate.slug} - ${(candidate.devices || []).length} device${
                    (candidate.devices || []).length === 1 ? '' : 's'
                  }`}
                />
              ))}
            </div>
          )}

          <p className="m-0 text-xs text-gray-600">
            {(attachments || []).length
              ? `Attached to ${(attachments || []).length} section${
                  (attachments || []).length === 1 ? '' : 's'
                }. Edits here reach all of them, and every lab that uses them.`
              : 'Attached to nothing yet. The device stays in the library and no run includes it.'}
          </p>
        </LabSection>
      )}

      <LabSection
        title="Connection"
        hint="How the agent inside the pod reaches this device. A publication can override the address per data centre, so this is the default rather than the only answer."
      >
        <LabFieldRow columns={3}>
          <LabOptionSelect
            label="Protocol"
            value={draft.transport}
            options={LAB_TRANSPORTS}
            onChange={(transport) => onChange({ transport })}
            helpText="Manual means nothing connects: a human does the step."
          />
          <TextField
            label="Address or FQDN"
            value={draft.address_template || ''}
            onChange={(evt) => onChange({ address_template: evt.target.value })}
            helperText="Internal to the pod. Overridable per lab and data centre."
            fullWidth
          />
          <TextField
            label="Port"
            type="number"
            value={draft.port ?? ''}
            onChange={(evt) =>
              onChange({ port: evt.target.value === '' ? null : Number(evt.target.value) })
            }
            helperText="Empty inherits the transport default."
            fullWidth
          />
        </LabFieldRow>

        <TextField
          label="Credential reference"
          value={draft.credential_ref || ''}
          onChange={(evt) => onChange({ credential_ref: evt.target.value })}
          helperText="The name of a stored credential, e.g. vault://lab/hybrid-mesh/fmc."
          fullWidth
        />

        {/* Stated rather than implied, and kept at full length where everything around it was cut.
            This row is sent to every agent that enrols, so a password typed here would be a
            credential disclosure with each enrollment - and it would look like it worked. */}
        <p className="m-0 text-xs text-gray-600">
          This names a credential. It never holds one: no username, no password, no key. The agent
          resolves the reference against credentials scoped to its own session and pod.
        </p>

        {/* The same select as every other reference field on this form. It used to be a bare
            `<select>`, which is the one control here that looked like a different application. */}
        <LabRowSelect
          label="Jump host"
          value={draft.jump_host}
          rows={jumpHostCandidates}
          onChange={(jump_host) => onChange({ jump_host })}
          labelFor={(candidate) => candidate.display_name || candidate.device_key}
          hintFor={(candidate) => candidate.address_template || undefined}
          emptyLabel="Reached directly"
          disabled={!jumpHostCandidates.length}
          helpText={
            jumpHostCandidates.length
              ? 'Another device this one is reached through. Attach it to the same sections - a section with this box but not its route treats it as directly reachable.'
              : 'Nothing else in the library yet.'
          }
        />

        <LabCheckbox
          label="Verify TLS"
          checked={!!draft.verify_tls}
          onChange={(verify_tls) => onChange({ verify_tls })}
          helpText="Turn this off only for a device with a self-signed certificate inside the pod."
        />
      </LabSection>

      <LabSection
        title="What the run expects"
        hint="Whether the pod is allowed to come up without this device, and whether anything watches it afterwards."
        collapsible
        defaultOpen
      >
        <LabCheckbox
          label="Expected at bootstrap"
          checked={!!draft.bootstrap_expected}
          onChange={(bootstrap_expected) => onChange({ bootstrap_expected })}
          helpText="Bootstrap waits for this device to answer before the run starts."
        />
        <LabCheckbox
          label="Required"
          checked={!!draft.required}
          onChange={(required) => onChange({ required })}
          helpText="The lab cannot run without it."
        />
        <LabCheckbox
          label="Monitored"
          checked={!!draft.monitored}
          onChange={(monitored) => onChange({ monitored })}
          helpText="Appears in health reports and fleet sweeps. A required device nothing monitors is one the run depends on and nobody watches."
        />

        <LabFieldRow>
          <TextField
            label="Order"
            type="number"
            value={draft.order ?? 0}
            onChange={(evt) => onChange({ order: Number(evt.target.value) })}
            helperText="Where it sits in a section's device list. One number, read by every section."
            fullWidth
          />
        </LabFieldRow>

        {/* Rows rather than a textarea, and typed per row: a step templating {{attributes.port}}
            into a request body gets 443 or "443" depending on what was written here, and an API that
            refuses the string does not say which. */}
        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.deviceAttributes}
          value={draft.attributes}
          resetKey={resetKey}
          onChange={(attributes, error) => {
            onJsonError(error);
            if (!error) onChange({ attributes });
          }}
        />
      </LabSection>

      <LabBlockerList
        blockers={rowBlockers}
        heading="This device will be flagged in the library:"
      />

      {jsonError && <p className="m-0 text-sm text-amber-800">{`Attributes: ${jsonError}`}</p>}

      {/* Stacked over this form rather than replacing it, so a type authored mid-draft costs nothing.
          The save invalidates the catalogue, and the new type is selected on the way back. */}
      <LabDeviceTypeEditorModal
        open={newDeviceType}
        deviceType={null}
        onClose={() => setNewDeviceType(false)}
        onSaved={(deviceType) => applyType(deviceType.id, deviceType)}
      />
    </div>
  );
};

export default LabDeviceForm;
